import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import FolderView from '../components/documents/FolderView';
import { BreadcrumbsHelper } from '../components/UI/Breadcrumbs';
import useAPI from '../hooks/useAPI';
import Page from '../components/_layout/Page';
import useUserContext from '../hooks/useUserContext';
import { isAdmin } from '../services/rolesSvc';

export default function Docs() {
  const api = useAPI();
  const userCtx = useUserContext();
  const { folderId } = useParams();
  const history = useHistory();

  const [folder, setFolder] = useState({});
  const [breadcrumbsItems, setBreadcrumbsItems] = useState([]);


  const permissions = useMemo(() => {
    const isAdminOrProj = isAdmin(userCtx);
    return {
      canCreateFolder: isAdminOrProj || folder.userPerms?.includes('write') || folder.accessType === 'public' || folder.visibleAccessType === 'public',
      canUploadFiles: folder.allowedLogins?.includes(userCtx.user.sAMAccountName) || isAdminOrProj || folder.userPerms?.includes('write') || folder.accessType === 'public' || folder.visibleAccessType === 'public',
      canDelete: isAdminOrProj,
      canAssignAccessToFolders: isAdminOrProj,
      canSeeFolder: isAdminOrProj,
      canCopyLink: isAdminOrProj,
      canRename: isAdminOrProj 
    }
  }, [userCtx, folder]);

  const fetchFolderContent = async (url = '/folder/common-docs') => {
    const resp = await api.get(url);
    !permissions.canSeeFolder ? resp.content = resp.content.filter((e) => e.accessType !== 'private' || e.visibleAccessType !== 'private' || e.userPerms?.length) : null;
    if (resp && !resp.error) {
      const newBreadcrumbsItems = BreadcrumbsHelper.getItemsFromNestedObject(resp, 'parentFolder');
      setBreadcrumbsItems(newBreadcrumbsItems);
      setFolder(resp);
    }
  };

  useEffect(() => {
    if (folderId) fetchFolderContent(`/folders/${folderId}`);
    else fetchFolderContent();
  }, [folderId]);

  const updateContent = (newItem) => setFolder((prev) => ({
    ...prev,
    content: [...prev.content, newItem].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }
      return a.id - b.id;
    }),
  }));

  const renameFolderInContent = (id, name) => setFolder(prev => ({
    ...prev,
    content: prev.content.map((item) => (item.id === id ? {...item, name: name} : item))
  }));

  const handleFolderCreate = async (name) => {
    const url = folderId
      ? `/folders/${folderId}/newFolder`
      : '/folder/common-docs/newFolder';
    const response = await api.post(url, { name });
    if (response && !response.error) {
      updateContent(response);
      return true;
    }
    return false;
  };
  
  const handleFolderRename = async (name, id) => {
      const response = await api.putResponseText(`/folders/${id}`, {newName: name});
    if(response && !response.error) {
      renameFolderInContent(id, name);
      return true;
    }
    return false;
  };

  const handleFilesUpload = async (files) => {
    const url = folderId 
      ? `/folders/minio/${folderId}/uploadFile` 
      : `/folder/common-docs/minio/uploadFile`;

    try {
      for (const file of files) {
        const response = await api.postFormData(url, {}, file, {isJsonValue:true});
        if (response && !response.error) updateContent(response);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleFolderOpen = async (item) => history.push(`/docs/${item.id}`)
  const handleFileOpen = async (item) => await api.download(`/minioFiles/download/${item.id}`, item.name)

  const handleFolderDelete = async (folderToDelete) => {
    const resp = await api.del(`/folders/minio/${folderToDelete.id}`)
    if(resp && !resp.error) {
      setFolder(prev => ({
        ...prev,
        content: prev.content.filter((item) => item.id !== folderToDelete.id || item.type !== 'folder'),
      }));
      return true;
    }
    return false;
  };

  const handleFileDelete = async (fileToDelete) => {
    const resp = await api.del(`/minioFiles/remove/${fileToDelete.id}`)
    if(resp && !resp.error) {
      setFolder(prev => ({
        ...prev,
        content: prev.content.filter((item) => item.id !== fileToDelete.id || item.type !== 'file'),
      }));
      return true;
    }
    return false;
  };

  return (
    <Page className="docs-page" header="Документы Программы развития УрФУ на 2021-2030" headerStyle={{ textAlign: 'left' }}>
      <FolderView
        content={folder.content}
        parentId={folder.id}
        accessType={folder.accessType}
        userPerms={folder.userPerms}
        permissions={permissions}
        onFolderCreate={handleFolderCreate}
        onFolderRename={handleFolderRename}
        onFilesUpload={handleFilesUpload}
        onFolderOpen={handleFolderOpen}
        onFileOpen={handleFileOpen}
        onFolderDelete={handleFolderDelete}
        onFileDelete={handleFileDelete}
        breadcrumbsProps={{
          items: breadcrumbsItems,
          getLink: (item) => `/docs${!item.root ? `/${item.id}` : ''}`,
        }}
      />
    </Page>
  );
}
